import { memo } from 'react';
import classNames from 'classnames';
import { CheckIcon } from 'lucide-react';

import { FormattedMessage, useIntl } from '@features/intl';
import { useStores } from '@/components/common/root-store-provider/root-store-provider';
import { TimerDisplay } from './timer-display';
import { useOptimizedCountdown } from '@utils/hooks/use-optimized-countdown';

import styles from '../index.module.css';

type PromoBannerProps = {
  onFinish?: () => void;
  targetTime: number;
  className?: string;
};

export const PromoBanner = memo(
  ({ onFinish, targetTime, className }: PromoBannerProps) => {
    const {
      quizStore: { userName },
    } = useStores();
    const intl = useIntl();

    const { minutes, seconds, isComplete } = useOptimizedCountdown({
      targetDate: targetTime,
      onComplete: onFinish,
    });

    const promoCode = `${userName}_${intl
      .formatDate(Date.now(), {
        month: 'short',
        year: 'numeric',
      })
      .toLowerCase()
      .replace(/\s+/g, '')}`;

    return (
      <div className={classNames(styles.bannerWithPromo, className)}>
        <p className={styles.promoTitle}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
          >
            <g clipPath="url(#clip0_11980_12969)">
              <path
                d="M8.42455 14.9151L15.1063 8.21969C15.6202 7.70572 15.6545 7.41103 15.6545 6.69147V4.08732C15.6545 3.3609 15.4831 3.13476 14.9623 2.61393L13.3861 1.03773C12.8721 0.523759 12.646 0.345581 11.9196 0.345581H9.30859C8.58901 0.345581 8.3012 0.379846 7.78721 0.893822L1.08497 7.58237C0.104994 8.56234 0.0912871 9.57658 1.09183 10.5771L5.42979 14.9082C6.43717 15.9087 7.44456 15.8951 8.42455 14.9151ZM7.68442 14.0859C7.19785 14.5793 6.67018 14.5861 6.16991 14.079L1.92105 9.83015C1.41392 9.32987 1.42762 8.8022 1.91418 8.31564L8.5616 1.67506C8.70552 1.538 8.84258 1.44892 9.07558 1.44892H11.9607C12.18 1.44892 12.3171 1.53115 12.4678 1.67506L14.3318 3.53909C14.4758 3.68299 14.5512 3.8269 14.5512 4.03935V6.93133C14.5512 7.15747 14.4689 7.30139 14.3318 7.43845L7.68442 14.0859ZM11.1178 5.828C11.6523 5.828 12.0566 5.40995 12.0566 4.88913C12.0566 4.36144 11.6523 3.95026 11.1178 3.95026C10.5832 3.95026 10.1789 4.36144 10.1789 4.88913C10.1789 5.40995 10.5832 5.828 11.1178 5.828Z"
                fill="white"
              />
            </g>
            <defs>
              <clipPath id="clip0_11980_12969">
                <rect width="16" height="16" fill="white" />
              </clipPath>
            </defs>
          </svg>
          <FormattedMessage id="SwpPaywall.Banner.Title" />
        </p>
        <div className={styles.promoAndTimer}>
          <div className={styles.promo}>
            {promoCode}
            <CheckIcon width={20} height={20} />
          </div>
          <div className={styles.promoTimer}>
            <TimerDisplay
              minutes={minutes}
              seconds={seconds}
              isTimerEnd={isComplete}
            />
          </div>
        </div>
      </div>
    );
  },
);

PromoBanner.displayName = 'PromoBanner';
